import * as XLSX from 'xlsx';

/**
 * The Chart of Accounts import spreadsheet: its template, and reading one back.
 *
 * Same split as the card settlement sheet (see there) — parsing happens here because the create
 * endpoint's contract is one account, not a file; a row is just that account's fields read off a
 * grid. Deliberately lean: only what a bulk upload of mostly-leaf accounts needs (code, name,
 * type, posting flag, parent, a couple of labels). Everything the single-account form also offers
 * (control-account flags, mandatory dimensions, reporting codes, …) keeps its own default and is
 * set afterwards on the account itself, the same way a manually-created one would be refined.
 *
 * Nothing here judges a row beyond what it needs to call Create at all — an account code that
 * already exists, or a parent code that doesn't, is the server's refusal to report, not something
 * guessed at here.
 */

export const ACCOUNT_TYPES = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'] as const;

interface ColumnSpec {
  header: string;
  aliases?: string[];
  width: number;
  note: string;
}

const COLUMNS: ColumnSpec[] = [
  { header: 'Code',             aliases: ['account code', 'account no'],   width: 14, note: 'Required, unique' },
  { header: 'Name',              aliases: ['account name'],                 width: 32, note: 'Required' },
  { header: 'Account Type',      aliases: ['type'],                          width: 14, note: 'Asset / Liability / Equity / Revenue / Expense — required' },
  { header: 'Posting Account',   aliases: ['posting', 'is posting'],         width: 14, note: 'Yes/No — blank means Yes (a header account is No)' },
  { header: 'Parent Code',       aliases: ['parent', 'parent account'],      width: 14, note: 'Blank for a top-level account' },
  { header: 'Short Name',        aliases: [],                                width: 16, note: 'Optional' },
  { header: 'Description',       aliases: [],                                width: 32, note: 'Optional' },
];

const normalise = (value: string) => value.trim().toLowerCase().replace(/[\s_#]+/g, ' ');

const HEADER_INDEX = new Map<string, number>(
  COLUMNS.flatMap((c, i) =>
    [c.header, ...(c.aliases ?? [])].map(h => [normalise(h), i] as [string, number])),
);

// ── Template ─────────────────────────────────────────────────────────────────

export function downloadAccountImportTemplate(): void {
  const aoa: unknown[][] = [
    COLUMNS.map(c => c.header),
    COLUMNS.map(c => c.note),
    ['1000', 'Current Assets',    'Asset',    'No',  '',     '', 'Header — groups the current-asset accounts below'],
    ['1010', 'Cash in Hand',      'Asset',    'Yes', '1000', '', ''],
    ['1020', 'Bank — Main A/C',   'Asset',    'Yes', '1000', '', ''],
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = COLUMNS.map(c => ({ wch: c.width }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Chart of Accounts');
  XLSX.writeFile(wb, 'chart-of-accounts-template.xlsx');
}

// ── Reading a sheet back ─────────────────────────────────────────────────────

export interface AccountImportRow {
  rowNumber:        number;   // the spreadsheet's own row number, for reporting back
  code:              string;
  name:              string;
  accountType:       string;
  isPostingAccount:  boolean;
  parentCode:        string | null;
  shortName:         string | null;
  description:       string | null;
}

export interface ParsedAccountSheet {
  rows: AccountImportRow[];
  /** Rows this parser could not read — reported, never silently dropped. */
  problems: { rowNumber: number; message: string }[];
}

const readText = (value: unknown): string | null => {
  if (value == null) return null;
  const text = String(value).trim();
  return text.length === 0 ? null : text;
};

const readYesNo = (value: unknown, defaultValue: boolean): boolean => {
  const text = readText(value);
  if (text === null) return defaultValue;
  const t = text.toLowerCase();
  if (['yes', 'y', 'true', '1'].includes(t)) return true;
  if (['no', 'n', 'false', '0'].includes(t)) return false;
  return defaultValue;
};

export async function parseAccountImportFile(file: File): Promise<ParsedAccountSheet> {
  const buffer = await file.arrayBuffer();
  const wb     = XLSX.read(buffer, { type: 'array' });

  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error('That file has no worksheets.');

  const grid = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[sheetName], { header: 1, blankrows: false });
  if (grid.length === 0) throw new Error('That sheet is empty.');

  let headerRow = -1;
  let map: number[] = [];

  for (let i = 0; i < Math.min(grid.length, 25); i++) {
    const candidate = (grid[i] ?? []).map(cell => HEADER_INDEX.get(normalise(String(cell ?? ''))) ?? -1);
    // A header row is one naming a Code and a Name column. Anything less is a title line.
    if (candidate.includes(0) && candidate.includes(1)) { headerRow = i; map = candidate; break; }
  }

  if (headerRow === -1)
    throw new Error(
      'Could not find the column headings. The sheet needs at least Code and Name columns — '
    + 'download the template to see the full set.');

  const rows: AccountImportRow[] = [];
  const problems: ParsedAccountSheet['problems'] = [];
  const seenCodes = new Map<string, number>();   // code (upper) -> first row number that used it

  const at = (cells: unknown[], column: number) => {
    const idx = map.indexOf(column);
    return idx === -1 ? null : cells[idx] ?? null;
  };

  for (let i = headerRow + 1; i < grid.length; i++) {
    const cells = grid[i] ?? [];
    const rowNumber = i + 1;

    if (cells.every(c => c == null || String(c).trim() === '')) continue;

    const code = readText(at(cells, 0));
    const name = readText(at(cells, 1));

    // A note row from the template.
    if (!code && !name) continue;

    if (!code) { problems.push({ rowNumber, message: 'No account code.' }); continue; }
    if (!name) { problems.push({ rowNumber, message: 'No account name.' }); continue; }

    const typeText = readText(at(cells, 2));
    const accountType = ACCOUNT_TYPES.find(t => t.toLowerCase() === (typeText ?? '').toLowerCase());
    if (!accountType) {
      problems.push({ rowNumber, message: `Account Type must be one of ${ACCOUNT_TYPES.join(', ')} — got "${typeText ?? ''}".` });
      continue;
    }

    const codeKey = code.toUpperCase();
    const firstRow = seenCodes.get(codeKey);
    if (firstRow !== undefined) {
      problems.push({ rowNumber, message: `Code "${code}" is also used on row ${firstRow}.` });
      continue;
    }
    seenCodes.set(codeKey, rowNumber);

    rows.push({
      rowNumber,
      code,
      name,
      accountType,
      isPostingAccount: readYesNo(at(cells, 3), true),
      parentCode:       readText(at(cells, 4)),
      shortName:        readText(at(cells, 5)),
      description:      readText(at(cells, 6)),
    });
  }

  if (rows.length === 0 && problems.length === 0)
    throw new Error('That sheet has headings but no account rows under them.');

  return { rows, problems };
}
